import React, { FunctionComponent, ReactNode } from 'react'
import clsx from 'clsx'
import { Icon, IconNames } from '../Icon'
import { Heading, Text } from '../Typography'
import styles from './style.module.scss'
import { KINDS, SIZES } from './constants'
import { DesignLanguage, useIsDesignLanguage, useThemeClass } from '../Theme'
import { ArcadeIconNames } from '../Icon/ArcadeIcon'

type KindsType = typeof KINDS
type KindsKeys = keyof KindsType
export type MessageKinds = KindsType[KindsKeys]

type SizesType = typeof SIZES
type SizesKeys = keyof SizesType
export type MessageSizes = SizesType[SizesKeys]

export interface MessageProps {
  kind?: MessageKinds
  size?: MessageSizes
  iconName?: IconNames | ArcadeIconNames
  heading?: string | ReactNode
  link?: string | ReactNode
  description?: string | ReactNode
  testNameSpace?: string
  isFullWidthContent?: boolean
}

export const Message: FunctionComponent<MessageProps> = ({
  kind = KINDS.Success,
  size = SIZES.Regular,
  iconName,
  heading,
  link,
  description,
  testNameSpace = '',
  isFullWidthContent = false,
}) => {
  const themeClass = useThemeClass()
  const isArcadeDesign = useIsDesignLanguage(DesignLanguage.Arcade)
  const descriptionIsString = typeof description === 'string'
  const headingIsString = typeof heading === 'string'
  const linkIsString = typeof link === 'string'
  const hasTestId = testNameSpace.length > 0
  const dataTestIdProp = hasTestId && { 'data-testid': `${testNameSpace}-message` }
  const testNameSpaceProp = hasTestId && { testNameSpace: `${testNameSpace}-message` }
  const iconDefined = typeof iconName !== 'undefined' && iconName !== null
  const containerClassName = clsx(styles.messageContainer, styles[`message${size}`], styles[`message${kind}`], styles[themeClass])
  const fullWidthContent = clsx(isFullWidthContent && styles.fullWidth)
  const apIconSize = size === SIZES.Large ? 'Medium' : 'Small'
  const cashIconSize = 'Medium'
  const iconSize = isArcadeDesign ? cashIconSize : apIconSize
  const apHeadingSize = size === SIZES.Large ? 'L' : 'S'
  const cashHeadingSize = size === SIZES.Large ? 'L' : 'XS'
  const headingSize = isArcadeDesign ? cashHeadingSize : apHeadingSize

  return (
    <div className={containerClassName} {...dataTestIdProp}>
      {iconDefined && (
        <div className={styles.iconContainer}>
          <Icon name={iconName} size={iconSize} color={kind === KINDS.Error ? 'Fire' : undefined} {...testNameSpaceProp} />
        </div>
      )}
      <div className={fullWidthContent}>
        {typeof heading !== 'undefined' &&
          (headingIsString ? (
            <Heading size={headingSize} {...testNameSpaceProp}>
              {heading}
            </Heading>
          ) : (
            heading
          ))}
        {typeof description !== 'undefined' &&
          (descriptionIsString ? (
            <Text size={size === SIZES.Large ? 'S' : 'XS'} {...testNameSpaceProp}>
              {description}
            </Text>
          ) : (
            description
          ))}
        {typeof link !== 'undefined' &&
          (linkIsString ? (
            <Text underline bold size={size === SIZES.Large ? 'S' : 'XS'} {...testNameSpaceProp}>
              {link}
            </Text>
          ) : (
            link
          ))}
      </div>
    </div>
  )
}
